import { Box, Checkbox, Collapse, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../../../../../../../../../api/hooks/useColors";
import CustomTooltip from "../../../../../../../../../components/CustomTooltip";
import iconMapping from "../../../../../../../../../icons/IconMapping";
import SvgToIcon from "../../../../../../../../../icons/SvgToIcon";
import { ReactComponent as CriticalAssetIcon } from "../../../../../../../../../scenes/stack/environment/icons/critical-asset-20-currentColor.svg";
import {
  getEntityTypeDisplayName,
  getEntityTypeFromEntityId,
  getSimpleEntityDisplayNameFromId,
  isComplexEntityType,
} from "../../../../../../../../../utils/utils";
import { ReactComponent as ChevronDownIcon } from "../../../../../../../../stack/environment/icons/chevron-down.svg";
import { ReactComponent as ChevronRightIcon } from "../../../../../../../../stack/environment/icons/chevron-right.svg";
import { getMisconfigurationChecked } from "./GroupedControlEntitiesSelection";

export default function MisconfigurationRow({
  fullMisconfiguration,
  rowByEntityId,
  selectedEntityIDsMapping,
  setSelectedEntityIDsMapping,
}) {
  const colors = useColors();
  const [collapsed, setCollapsed] = useState(false);
  const handleMisconfigurationCheckChange = (fullMisconfig) => {
    if (
      Object.keys(selectedEntityIDsMapping).includes(fullMisconfig.entity_id)
    ) {
      const newMapping = { ...selectedEntityIDsMapping };
      if (
        newMapping[fullMisconfig.entity_id].length ===
        fullMisconfig.affected_entity_ids.length
      )
        delete newMapping[fullMisconfig.entity_id];
      else
        newMapping[fullMisconfig.entity_id] = fullMisconfig.affected_entity_ids;
      setSelectedEntityIDsMapping(newMapping);
    } else {
      setSelectedEntityIDsMapping((prev) => {
        const newMapping = { ...prev };
        newMapping[fullMisconfig.entity_id] = fullMisconfig.affected_entity_ids;
        return newMapping;
      });
    }
  };

  const handleAffectedEntityCheckChange = (entityId, fullMisconfig) => {
    setSelectedEntityIDsMapping((prev) => {
      const newMapping = { ...prev };
      if (Object.keys(prev).includes(fullMisconfig.entity_id)) {
        if (prev[fullMisconfig.entity_id].includes(entityId)) {
          newMapping[fullMisconfig.entity_id] = newMapping[
            fullMisconfig.entity_id
          ].filter((id) => id !== entityId);
          if (newMapping[fullMisconfig.entity_id].length === 0) {
            delete newMapping[fullMisconfig.entity_id];
          }
        } else {
          newMapping[fullMisconfig.entity_id].push(entityId);
        }
      } else {
        newMapping[fullMisconfig.entity_id] = [entityId];
      }
      return newMapping;
    });
  };

  const Icon =
    iconMapping[fullMisconfiguration.entity_type] || iconMapping["Internet"];

  const isMisconfigurationCriticalAsset =
    rowByEntityId?.[fullMisconfiguration.entity_id]?.isCriticalAsset;

  const hasAffectedEntities =
    fullMisconfiguration.affected_entity_ids.length > 0;

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" paddingLeft="8px" height="50px">
        <Box width="50px" display="flex" justifyContent="center">
          <Checkbox
            checked={getMisconfigurationChecked(
              fullMisconfiguration,
              selectedEntityIDsMapping
            )}
            size="small"
            sx={{
              color: colors.primary_main,
              "&.Mui-checked": {
                color: colors.primary_main,
              },
            }}
            onChange={() =>
              handleMisconfigurationCheckChange(fullMisconfiguration)
            }
          />
        </Box>
        <Box
          width="50px"
          display="flex"
          justifyContent="center"
          paddingLeft="25px"
        >
          <SvgToIcon
            IconLogo={CriticalAssetIcon}
            width="13px"
            style={{
              color: isMisconfigurationCriticalAsset
                ? colors.icon_action_selected
                : colors.icon_action_disabled,
            }}
          />
        </Box>
        <Box
          width="100px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CustomTooltip
            title={getEntityTypeDisplayName(fullMisconfiguration.entity_type)}
          >
            <Icon
              style={{
                color: colors.text_primary,
                width: "18px",
                height: "18px",
              }}
            />
          </CustomTooltip>
        </Box>
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          onClick={() => hasAffectedEntities && setCollapsed((prev) => !prev)}
          sx={{ cursor: hasAffectedEntities ? "pointer" : "default" }}
          paddingLeft={hasAffectedEntities ? "0px" : "32px"}
        >
          {hasAffectedEntities && (
            <SvgToIcon
              IconLogo={collapsed ? ChevronRightIcon : ChevronDownIcon}
              width="24px"
              style={{ color: colors.active }}
            />
          )}
          <Typography variant="overline" color={colors.text_primary}>
            {fullMisconfiguration.display_name}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" gap={1}>
        <Collapse in={!collapsed}>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {fullMisconfiguration.affected_entity_ids.sort().map((entityId) => {
              const entityType = getEntityTypeFromEntityId(entityId);
              const EntityTypeIcon =
                iconMapping[entityType] || iconMapping["Internet"];
              let displayName = getSimpleEntityDisplayNameFromId(entityId);
              const entity = rowByEntityId[entityId];
              const isCriticalAsset = entity.isCriticalAsset;
              if (isComplexEntityType(entityType)) {
                displayName = entity.displayName;
              }
              const checked =
                Object.keys(selectedEntityIDsMapping).includes(
                  fullMisconfiguration.entity_id
                ) &&
                selectedEntityIDsMapping[
                  fullMisconfiguration.entity_id
                ].includes(entityId);
              return (
                <Box
                  key={fullMisconfiguration.entity_id + entityId}
                  display="flex"
                  alignItems="center"
                  paddingLeft="8px"
                  height="40px"
                >
                  <Box width="50px" display="flex" justifyContent="center">
                    <Checkbox
                      checked={checked}
                      size="small"
                      sx={{
                        color: colors.primary_main,
                        "&.Mui-checked": {
                          color: colors.primary_main,
                        },
                      }}
                      onChange={() =>
                        handleAffectedEntityCheckChange(
                          entityId,
                          fullMisconfiguration
                        )
                      }
                    />
                  </Box>
                  <Box
                    width="50px"
                    display="flex"
                    justifyContent="center"
                    paddingLeft="25px"
                  >
                    <SvgToIcon
                      IconLogo={CriticalAssetIcon}
                      width="13px"
                      style={{
                        color: isCriticalAsset
                          ? colors.icon_action_selected
                          : colors.icon_action_disabled,
                      }}
                    />
                  </Box>
                  <Box
                    width="100px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <CustomTooltip title={getEntityTypeDisplayName(entityType)}>
                      <EntityTypeIcon
                        style={{
                          color: colors.text_primary,
                          width: "16px",
                          height: "16px",
                        }}
                      />
                    </CustomTooltip>
                  </Box>
                  <Box paddingLeft="32px">
                    <Typography variant="caption" color={colors.text_primary}>
                      {displayName}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}
